import {Injectable} from '@angular/core';
import {LoginStep, OtpDeliveryMethod} from './login.model';
import {BehaviorSubject, EMPTY, Observable, retry, Subscription, take, throwError} from 'rxjs';
import {catchError, distinctUntilChanged, filter, finalize, switchMap, tap} from 'rxjs/operators';
import {AuthService} from '../auth.service';
import {ErrorMessages, ErrorService} from '../../shared/errors.service';
import {Router} from '@angular/router';
import {AppPaths} from '../../../enums/app-paths';
import {NotificationService, NotificationType} from '../../shared/notification.service';
import {Esito, GenericErrorResponse, GenericResponse} from '../../api-interfaces';
import {User} from '../../identity/user.model';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TimerService} from '../../shared/timer/timer.service';
import {ConfigService} from '../../config.service';
import {DataResult} from 'src/app/identity/edit-credentials/qr-code-association/qr-code-association.service';
import {differenceInSeconds} from 'date-fns';
import {Timer} from '../../shared/timer/timer.model';
import {environment} from '../../../environments/environment';

@UntilDestroy()
@Injectable()
export class LoginService {
  private readonly WAIT_SECONDS_FOR_NEW_REQUEST = 15_000;

  private _step$ = new BehaviorSubject<LoginStep>(LoginStep.UsernameAndPassword);
  readonly step$ = this._step$.asObservable();

  get step(): LoginStep {
    return this._step$.getValue();
  }
  set step(value: LoginStep) {
    this._step$.next(value);
  }

  private _isLoading$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this._isLoading$.asObservable();

  get isLoading(): boolean {
    return this._isLoading$.getValue();
  }

  USERNAME_AND_PASSWORD_LOADING_TIP = $localize`:@@Auth.Generic.SpinnerText:Invio richiesta`;
  private _loadingTip$ = new BehaviorSubject<string>(this.USERNAME_AND_PASSWORD_LOADING_TIP);
  readonly loadingTip$ = this._loadingTip$.asObservable();

  get loadingTip(): string {
    return this._loadingTip$.getValue();
  }

  private _autoLoginQrCode$ = new BehaviorSubject<string>(undefined);
  readonly autoLoginQrCode$ = this._autoLoginQrCode$.asObservable();

  private _autoLoginRequestId$ = new BehaviorSubject<string>(undefined);
  readonly autoLoginRequestId$ = this._autoLoginRequestId$.asObservable();

  private _level2QrCode$ = new BehaviorSubject<string>(undefined);
  readonly level2QrCode$ = this._level2QrCode$.asObservable();
  get level2QrCode(): string { return this._level2QrCode$.getValue(); }

  private _level2RequestId$ = new BehaviorSubject<string>(undefined);
  readonly level2RequestId$ = this._level2RequestId$.asObservable();
  get level2RequestId(): string { return this._level2RequestId$.getValue(); }

  private _otpDeliveryMethod$ = new BehaviorSubject<OtpDeliveryMethod[]>(undefined);
  readonly otpDeliveryMethod$ = this._otpDeliveryMethod$.asObservable();

  private _selectedOtpDeliveryMethod$ = new BehaviorSubject<OtpDeliveryMethod>(undefined);
  readonly selectedOtpDeliveryMethod$ = this._selectedOtpDeliveryMethod$.asObservable();

  private _otpTimer$ = new BehaviorSubject<number>(0);
  readonly otpTimer$ = this._otpTimer$.asObservable();
  get otpTimer(): number { return this._otpTimer$.getValue(); }

  timer: Subscription;
  autologinTokenGeneratorSubscription: Subscription;
  username: string;
  password: string;
  readonly pollingWaitTimeInMS = ConfigService.settings.pollingTimeMs;
  readonly GENERIC_ERROR_MESSAGE = $localize`:@@LoginService.GenericErrorMessage.Text: Errore generico`;

  constructor(
    private authService: AuthService,
    private router: Router,
    private notificationService: NotificationService,
    private errorService: ErrorService,
    private timerService: TimerService,
  ) {
    this.registerOnUsernameAndPasswordStepLanding$().subscribe();
    this.registerOnLevel2StepLanding$().subscribe();
    this.registerOnOTPStepLanding$().subscribe();
  }

  generateAutoLoginQrCode(): void {
    if (!environment.showAuthenticationWithApp) {
      return;
    }
    this.autologinTokenGeneratorSubscription = this.authService.generateAutoLoginQrCode$().pipe(
      retry({delay: this.pollingWaitTimeInMS}),
      untilDestroyed(this),
      take(1),
      tap(response => this._autoLoginQrCode$.next(response.qrcode)),
      tap(response => this._autoLoginRequestId$.next(response.request_id)),
      switchMap(response => this.verifyAutoLoginQrCode$(response.request_id)),
      catchError(error => {
        this.manageNotificationForErrors(error, $localize`:@@LoginService.FastLoginQrCodeGeneration.GenericError: Non è stato possibile generare il QR CODE per eseguire il login rapido. Se il problema persiste, contattare il servizio di assistenza.`);
        return throwError(() => error);
      }),
    ).subscribe();
  }

  verifyAutoLoginQrCode$(requestId: string): Observable<GenericResponse> {
    const startTime = Date.now();
    return this.authService.verifyAutoLoginQrCode$(requestId).pipe(
      untilDestroyed(this),
      take(1),
      switchMap(response => {
        const result = response.data.result;
        if (result === DataResult.Success) {
          const jwt = response.data.token;
          return this.authService.authenticateWithAutoLogin$(jwt).pipe(
            tap(autologinResponse => {
              if (autologinResponse.esito === Esito.Ok && autologinResponse.result) {
                this.onUserAuthenticateWithSuccess(autologinResponse.result as User);
              }
            })
          );
        } else if (result === DataResult.QrCodeTimeout) {
          this.generateAutoLoginQrCode();
        } else if (result === DataResult.Failure) {
          const endTime = Date.now();
          const secondsElapsed = differenceInSeconds(endTime, startTime) * 1000;
          setTimeout(() => this.verifyAutoLoginQrCode$(requestId).subscribe(), secondsElapsed > this.WAIT_SECONDS_FOR_NEW_REQUEST ? 0 : this.WAIT_SECONDS_FOR_NEW_REQUEST - secondsElapsed);
        }
        return EMPTY;
      }),
      catchError(err => {
        const statusCode = err.error.meta.status;
        const endTime = Date.now();
        const secondsElapsed = differenceInSeconds(endTime, startTime) * 1000;
        if (statusCode === 504) {
          setTimeout(() => this.verifyAutoLoginQrCode$(requestId).subscribe(), secondsElapsed > this.WAIT_SECONDS_FOR_NEW_REQUEST ? 0 : this.WAIT_SECONDS_FOR_NEW_REQUEST - secondsElapsed);
        } else if (statusCode === 404) {
          setTimeout(() => this.generateAutoLoginQrCode(), secondsElapsed > this.WAIT_SECONDS_FOR_NEW_REQUEST ? 0 : this.WAIT_SECONDS_FOR_NEW_REQUEST - secondsElapsed);
        }
        return EMPTY;
      })
    );
  }

  registerOnUsernameAndPasswordStepLanding$(): Observable<LoginStep> {
    return this._step$.pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      filter(step => step === LoginStep.UsernameAndPassword),
      tap(() => this.generateAutoLoginQrCode())
    );
  }

  registerOnLevel2StepLanding$(): Observable<LoginStep> {
    return this._step$.pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      filter(step => step === LoginStep.Level2Recognition),
      tap(() => this.autologinTokenGeneratorSubscription.unsubscribe()),
      tap(() => this._loadingTip$.next(this.USERNAME_AND_PASSWORD_LOADING_TIP)),
      tap(() => this._level2QrCode$.next(null)),
      tap(() => this._level2RequestId$.next(null))
    );
  }

  registerOnOTPStepLanding$(): Observable<LoginStep> {
    return this._step$.pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      filter(step => step === LoginStep.Otp),
      tap(() => this._loadingTip$.next(this.USERNAME_AND_PASSWORD_LOADING_TIP)),
      filter(() => environment.showAuthenticationWithApp), // Cancellare solo questa riga quando sarà tolta la variabile d'ambiente
      tap(() => this.autologinTokenGeneratorSubscription.unsubscribe()),
    );
  }

  private doRequest$<T>(observable: Observable<T>, showNotificationError = false, genericErrorMessage = this.GENERIC_ERROR_MESSAGE, enableFinalize = true): Observable<T> {
    this._isLoading$.next(true);
    return observable.pipe(
      finalize(() => {
        if (enableFinalize) {
          this._isLoading$.next(false);
        }
      }),
      catchError(err => {
          const error = err.error;
          if (showNotificationError) {
            this.manageNotificationForErrors(error, genericErrorMessage);
          }
          throw new Error('esito: ko');
      })
    );
  }

  manageRedirect(error: GenericErrorResponse): void {
    const code = error.result.error_code;
    if (code === ErrorMessages.EXPIRED_PASSWORD.code) {
      this.router.navigateByUrl(`/${AppPaths.Auth}/${AppPaths.PasswordModify}`);
    }
  }

  manageNotificationForErrors(error: GenericErrorResponse, customGenericErrorMessage = this.GENERIC_ERROR_MESSAGE): void {
    if (!error) {
      return;
    }
    const errorCode = error?.result?.error_code;
    if (![ErrorMessages.EXPIRED_PASSWORD.code].includes(errorCode)) {
      const errorByCode = this.errorService.getErrorBycode(errorCode);
      const message = !errorByCode?.code || errorByCode?.code === '0000' ? customGenericErrorMessage ?? errorByCode.message : errorByCode.message;
      this.notificationService.createNotification(
        NotificationType.ERROR,
        'Errore',
        message
      );
    }
  }

  onLogin(username: string, password: string, otp?: string, otpDeliveryMethod?: OtpDeliveryMethod): void {
    this.username = username;
    this.password = password;
    this.doRequest$(
      this.authService.auth(username, password, otp, otpDeliveryMethod).pipe(
        tap(response => {
          const esito = response.esito;
          const result = response.result;
          if (esito === Esito.Ok) {
            const user = result as User;
            this.onUserAuthenticateWithSuccess(user);
          } else if (result && result.error_code === ErrorMessages.OTP_REQUESTED.code) {
            if (environment.showAuthenticationWithApp) {
              const otpDeliveryMethods = result.otp_delivery_methods;
              this.redirectOnOtpDeliveryMethods(otpDeliveryMethods);
              this._otpDeliveryMethod$.next(otpDeliveryMethods);
            } else {
              this.onClickCard(OtpDeliveryMethod.SMS);
            }
          }
        }),
        catchError(err => {
          const error = err.error;
          if (!environment.showAuthenticationWithApp) {
            this._isLoading$.next(false);
          }
          this.manageNotificationForErrors(error);
          this.manageRedirect(error);
          throw new Error('esito: ko');
        })
      ),
      false,
      this.GENERIC_ERROR_MESSAGE, // Cancellare questa riga e quella sotto quando sarà rimossa la variabile d'ambiente
      environment.showAuthenticationWithApp,
    ).subscribe();
  }

  private onUserAuthenticateWithSuccess(user: User): void {
    this.authService.setCurrentUser(user);
    this.router.navigateByUrl('/' + AppPaths.Identity);
    this.notificationService.createNotification(
      NotificationType.SUCCESS,
      'Login avvenuto con successo', '');
    this.authService.startUserPolling();
    this.authService.startUserActivityPolling();
  }

  private redirectOnOtpDeliveryMethods(otpDeliveryMethods: OtpDeliveryMethod[]): void {
    if (!otpDeliveryMethods) {
      this.notificationService.createNotification(
        NotificationType.ERROR,
        'Non è possibile proseguire con l\'autenticazione',
        'All\'utenza non è associato nessun metodo di invio OTP.'
      );
      this._step$.next(LoginStep.UsernameAndPassword);
    } else {
      this._step$.next(LoginStep.Level2Recognition);
    }
  }

  onClickCard(otpDeliveryMethod: OtpDeliveryMethod): void {
    this._selectedOtpDeliveryMethod$.next(otpDeliveryMethod);
    this.sendOtp(otpDeliveryMethod);
  }

  sendOtp(otpDeliveryMethod: OtpDeliveryMethod): void {
    if ([OtpDeliveryMethod.SMS, OtpDeliveryMethod.EMAIL, OtpDeliveryMethod.APP].includes(otpDeliveryMethod)) {
      if (!this.timerService.timerObject[otpDeliveryMethod as keyof  Timer]) {
        this._loadingTip$.next($localize`:@@Auth.Generic.SpinnerText.SendOtp:Invio otp`);
      }
      if (otpDeliveryMethod !== OtpDeliveryMethod.APP && !this.timerService.timerObject[otpDeliveryMethod as keyof  Timer] || otpDeliveryMethod === OtpDeliveryMethod.APP) {
        this.doRequest$(this.authService.auth(this.username, this.password, null, otpDeliveryMethod).pipe(
          take(1),
          tap(() => this.createOtpTimer(otpDeliveryMethod))
        ), true).subscribe();
      } else {
        this._step$.next(LoginStep.Otp);
        if (!environment.showAuthenticationWithApp) {
          this._isLoading$.next(false);
        }
      }
    } else {
      this.notificationService.createNotification(
        NotificationType.ERROR,
        'Non è stato possibile generare l\'OTP',
        'Il metodo di generazione OTP selezionato non è supportato.'
      );
      this._step$.next(LoginStep.UsernameAndPassword);
    }
  }

  private createOtpTimer(otpDeliveryMethod: OtpDeliveryMethod): void {
    this.timerService.createTimer(otpDeliveryMethod);
    this._step$.next(LoginStep.Otp);
  }

  onLoginWithOtp(otp: string, otpDeliveryMethod: OtpDeliveryMethod): void {
    this.onLogin(this.username, this.password, otp, otpDeliveryMethod);
  }
}
